'use client'

import * as React from 'react'

type TDevice = 'mobile' | 'desktop' | 'tablet'

interface UseDetectDeviceResponse {
  type: TDevice
  hasTouch: boolean
  isIOS: boolean
  isAndroid: boolean
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  isSafari: boolean
  isChrome: boolean
  isFirefox: boolean
  isEdge: boolean
  isInStandaloneMode: boolean
  browserName: string
  devicePixelRatio: number
  screenWidth: number
  screenHeight: number
  orientation: 'portrait' | 'landscape'
}

export function useDetectDevice(): UseDetectDeviceResponse {
  // Initialize with default values for SSR safety
  const [device, setDevice] = React.useState<UseDetectDeviceResponse>({
    type: 'desktop',
    hasTouch: false,
    isIOS: false,
    isAndroid: false,
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isSafari: false,
    isChrome: false,
    isFirefox: false,
    isEdge: false,
    isInStandaloneMode: false,
    browserName: 'unknown',
    devicePixelRatio: 1,
    screenWidth: 1920,
    screenHeight: 1080,
    orientation: 'landscape',
  })

  // Memoize the device detection function to avoid recreating it on every render
  const handleDeviceDetection = React.useCallback(() => {
    // Return early if window is not defined (SSR)
    if (typeof window === 'undefined') return

    const userAgent = navigator.userAgent.toLowerCase()
    const vendor = navigator.vendor.toLowerCase()

    // More comprehensive device detection
    const isMobileDevice = /iphone|ipod|android|blackberry|windows phone/g.test(userAgent)
    const isTabletDevice = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent)
    const isIOSDevice =
      /iphone|ipad|ipod/g.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    const isAndroidDevice = /android/g.test(userAgent)

    // Browser detection
    const isSafari = /safari/g.test(userAgent) && /apple/g.test(vendor)
    const isChrome = /chrome/g.test(userAgent) && /google/g.test(vendor)
    const isFirefox = /firefox/g.test(userAgent)
    const isEdge = /edg/g.test(userAgent)

    // Get browser name
    let browserName = 'unknown'
    if (isSafari) browserName = 'safari'
    else if (isChrome) browserName = 'chrome'
    else if (isFirefox) browserName = 'firefox'
    else if (isEdge) browserName = 'edge'

    // Determine device type
    let deviceType: TDevice = 'desktop'
    if (isMobileDevice) deviceType = 'mobile'
    else if (isTabletDevice) deviceType = 'tablet'

    // Get screen information
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight
    const orientation = screenWidth > screenHeight ? 'landscape' : 'portrait'

    // Check if the app is in standalone mode (PWA)
    const isInStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ('standalone' in navigator && (navigator as any).standalone === true)

    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0

    setDevice({
      type: deviceType,
      hasTouch,
      isIOS: isIOSDevice,
      isAndroid: isAndroidDevice,
      isMobile: isMobileDevice,
      isTablet: isTabletDevice,
      isDesktop: !isMobileDevice && !isTabletDevice,
      isSafari,
      isChrome,
      isFirefox,
      isEdge,
      isInStandaloneMode,
      browserName,
      devicePixelRatio: window.devicePixelRatio || 1,
      screenWidth,
      screenHeight,
      orientation,
    })

    return
  }, []) // Empty dependency array since we don't use any external values

  React.useEffect(() => {
    // Initial detection
    handleDeviceDetection()

    // Debounced resize handler for better performance
    let resizeTimer: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(handleDeviceDetection, 250) // Debounce for 250ms
    }

    window.addEventListener('resize', handleResize)
    // Also listen for orientation changes
    window.addEventListener('orientationchange', handleDeviceDetection)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleDeviceDetection)
      clearTimeout(resizeTimer)
    }
  }, [handleDeviceDetection])

  return device
}
